import * as Yup from 'yup';
import { constants } from './constants';
import { isEighteenOrOlder } from './isEighteenOrOlder';

// Name + DOB are only required at registration when insurance is enabled
// (needed to price the Plus plans by age). Otherwise signup is unchanged.
const INSURANCE_ENABLED = process.env.REACT_APP_INSURANCE_ENABLED === 'true';

const {
  ERROR_PLACEHOLDERS: {
    INVALID_EMAIL,
    EMAIL,
    PASSWORD_MUST_BE,
    PASSWORD,
    FIRST_NAME,
    LAST_NAME,
    PHONE,
    ADDRESS,
    DOB,
    ISDOBEIGHTEENYEAROLD,
    EIGHTEENOROLDER,
    CONFIRM_PASSWORD,
    NEW_PASSWORD_MATCH_ERROR,
    CITY,
    STATE,
    ZIP,
    HOME_PHONE,
    REGION,
    POSTAL_CODE,
    COUNTRY,
    PLEASE_SELECT,
    INSURANCECOMPANYNAME,
    POLICY_NUMBER,
    EMERGENCY_CONTACT,
  },
} = constants;

// Upper bound on password length — rejects oversized input before it hits the
// auth backend (basic hardening; also silences the "no max length" report).
const PASSWORD_MAX = 128;
const PASSWORD_MAX_MSG = 'Password must be at most 128 characters';

export const signinSchema = Yup.object({
  email: Yup.string().trim().email(INVALID_EMAIL).required(EMAIL),
  password: Yup.string().min(8, PASSWORD_MUST_BE).max(PASSWORD_MAX, PASSWORD_MAX_MSG).required(PASSWORD),
});

export const resetPasswordSchema = Yup.object({
  email: Yup.string().trim().email(INVALID_EMAIL).required(EMAIL),
});
export const signUpStep1Schema = Yup.object({
  firstName: Yup.string().required(FIRST_NAME),
  lastName: Yup.string().required(LAST_NAME),
  dob: Yup.string()
    .required(DOB)
    .test(ISDOBEIGHTEENYEAROLD, EIGHTEENOROLDER, (value) => {
      return isEighteenOrOlder(value);
    }),
  phone: Yup.string().min(10).required(PHONE),
  gender: Yup.string().required('Gender is required'),
  nationality: Yup.string().required('Nationality is required'),
  socialNum: Yup.string(),
  // .required("National ID Number is required"),
});

// Schema for PersonalInfoModal (without phone field)
export const personalInfoModalSchema = Yup.object({
  firstName: Yup.string().required(FIRST_NAME),
  lastName: Yup.string().required(LAST_NAME),
  dob: Yup.string()
    .required(DOB)
    .test(ISDOBEIGHTEENYEAROLD, EIGHTEENOROLDER, (value) => {
      return isEighteenOrOlder(value);
    }),
  gender: Yup.string().required('Gender is required'),
  nationality: Yup.string().required('Nationality is required'),
  socialNum: Yup.string(),
  // .required("National ID Number is required"),
});

// When insurance is enabled, name + DOB are collected at sign-up. The order
// wizard's Personal step hides them *only when the profile already has them*;
// in that case they must NOT be re-validated here, or the hidden required
// fields silently block "Next". Gender + nationality are always shown.
export const personalInfoInsuranceSchema = Yup.object({
  gender: Yup.string().required('Gender is required'),
  nationality: Yup.string().required('Nationality is required'),
  socialNum: Yup.string(),
});

export const signUpStep2Schema = Yup.object({
  address: Yup.string().required(ADDRESS),
  city: Yup.string().required(CITY),
  state: Yup.string().required(REGION),
  country: Yup.string().required(COUNTRY),
  zipCode: Yup.string().required(POSTAL_CODE),
});
export const signUpStep3Schema = Yup.object({
  insuranceType: Yup.string(),
  insuranceCompany: Yup.string(),
  policyNumber: Yup.string(),
  emergencyNumber: Yup.string(),
});

export const signUpStep4Schema = Yup.object({
  emergencyName: Yup.string().required('Emergency Name is required'),
  emergencyPhone: Yup.string().required('Emergency Phone is required'),
  emergencyRelation: Yup.string().required(
    'Emergency Relationship is required'
  ),
});

const strictEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Dedicated "Your details" step (name + DOB) — only shown when insurance is on.
export const signUpDetailsSchema = Yup.object({
  firstName: Yup.string().required(FIRST_NAME),
  lastName: Yup.string().required(LAST_NAME),
  dob: Yup.string()
    .required(DOB)
    .test(ISDOBEIGHTEENYEAROLD, EIGHTEENOROLDER, (value) => {
      return isEighteenOrOlder(value);
    }),
});

export const signUpSchema = Yup.object({
  email: Yup.string()
    .trim()
    .matches(strictEmailRegex, INVALID_EMAIL)
    .required(EMAIL),
  password: Yup.string()
    .min(12, 'Password must be at least 12 characters')
    .max(PASSWORD_MAX, PASSWORD_MAX_MSG)
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[^a-zA-Z0-9]/, 'Password must contain at least one special character')
    .required(PASSWORD),
  confirmPassword: Yup.string()
    .required(CONFIRM_PASSWORD)
    .oneOf([Yup.ref('password'), null], NEW_PASSWORD_MATCH_ERROR),
  phone: Yup.string().min(10).required(PHONE),
});

export const businessSignUpStep2Schema = Yup.object({
  companyName: Yup.string().trim().required('Company Name is required'),
  industry: Yup.string().required('Industry is required'),
});

export const businessSignUpStep3Schema = Yup.object({
  addressLine1: Yup.string().required('Address Line 1 is required'),
  addressLine2: Yup.string(), // Optional field
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State / Province is required'),
  country: Yup.string().required('Country is required'),
  postalCode: Yup.string().required('Postal Code is required'),
});

export const paymentSchema = Yup.object({
  cardHolderName: Yup.string().required('Card Holder Name is required'),
});
